import { observer } from 'mobx-react';
import React from 'react';
import { Link, PromiseMessage, PromiseRetry, routerStore } from 'lib/components';
import { dateFormat, msToHumanTime, ReactNodeGenerator } from 'lib/helpers';
import { pagedListResponseUtils, PagedListResponse } from '../../../domain';
import { routes } from 'pages/Routes';
import { ExamStudentListSceneStore } from './ExamStudentListSceneStore';
import { ExamStudentListResponseItem } from 'scenes/exam/ExamModels';
import { EssayStatus } from 'domain/enums';
import Swal from 'sweetalert2';
import { EssaySelect } from 'lib/components/select/EssayStatusSelect';
import { withTranslation, WithTranslation } from 'react-i18next';
import { TFunction } from 'i18next';

interface IProps extends WithTranslation {

}

@observer
class ExamStudentListSceneComponent extends React.Component<IProps> {
  store: ExamStudentListSceneStore;
  t: TFunction;

  constructor(props: IProps) {
    super(props);
    this.store = new ExamStudentListSceneStore();
    this.t = props.t;
  }

  componentDidMount() {
    this.store.load();
  }

  render() {
    return Array.from(this.renderer(this.store));
  }

  *renderRows(store: ExamStudentListSceneStore): ReactNodeGenerator {
    if (store.loader?.isResolved !== true)
      return;

    const essayItems = store.loader.value.Items;

    for (let item of essayItems) {

      yield (
        <tr key={item.ExamId}>
          <td>
            {essayItems.indexOf(item) + 1}
          </td>
          <td>
            {item.TeacherName}
          </td>
          <td>
            {dateFormat(item.CreatedAt, 'wymdhm', true)}
          </td>
          <td>
            {item.StatusStr}
          </td>
          <td>
            <button type='button' className="btn btn-primary" onClick={(e) => this.onEssayOpenBtnClick(e, item)}>
              {this.t('button.open')}
            </button>
          </td>
        </tr>
      );
    }
  }

  *renderer(store: ExamStudentListSceneStore): ReactNodeGenerator {
    if (store.loader == null)
      return;
    const state = store.loader;

    yield (<PromiseMessage key="loader-state"
      label={this.t('exam.loading')} state={state} align="right" />);

    yield (<PromiseRetry key="retry-actions"
      state={state} retry={store.load} align="right" />);

    if (!state.isResolved)
      return;

    const examItems = state.value.Items;
    const examRespons: PagedListResponse<ExamStudentListResponseItem> =
    {
      PageSize: 8,
      CurrentPage: store.currentPage,
      CountRecords: state.value.AllItemsCount,
      Data: examItems,
    };


    if (examItems.length === 0) {
      yield (
        <div className="row justify-content-center" key="no-classrooms">
          <div className="col-auto">
            <div className="alert alert-info">{this.t('exam.no-exam')}</div>
          </div>
        </div>
      );
      return;
    }

    yield (
      <div className="row" key="schedule">
        <div className="col">
          <table className="table table-sm table-bordered table-hover">
            <thead>
              <tr>
                <th>{this.t('exam.table.number')}</th>
                <th>{this.t('exam.table.teacher')}</th>
                <th>{this.t('exam.table.create-date')}</th>
                <th>{this.t('exam.table.status')}</th>
                <th>{this.t('exam.table.action')}</th>
              </tr>
            </thead>
            <tbody>
              {Array.from(this.renderRows(store))}
            </tbody>
          </table>
        </div>
      </div>);
  }

  componentWillUnmount() {
    this.store.dispose();
  }

  onEssayOpenBtnClick = async (e: React.MouseEvent<HTMLButtonElement, MouseEvent>, exam: ExamStudentListResponseItem) => {
    let link: string;
    if (exam.Status === EssayStatus.New) {
      const htmlBuilder: string[] = [];
      htmlBuilder.push(`<p>${this.t('exam.you-are-about-to-start-writing-exam')}</p>`);
      if (exam.TimeLimitMinutes) {
        const timeLimitHuman = msToHumanTime(exam.TimeLimitMinutes * 60 * 1000);
        htmlBuilder.push(`<p>${this.t('exam.has-time-limit')}: <strong>${timeLimitHuman}</strong></p>`);
      }
      htmlBuilder.push(`<p>${this.t('exam.start-now')}</p>`);
      const confirmResult = await Swal.fire({
        icon: 'info',
        html: htmlBuilder.join(""),
        confirmButtonText: this.t('button.start'),
        cancelButtonText: this.t('button.no'),
        showCancelButton: true
      });
      if (!confirmResult.isConfirmed) {
        return;
      }
    }
    link = routes.ExamStudentLink(exam.ExamId);
    routerStore.navigateTo(link);
  }
}

export default withTranslation()(ExamStudentListSceneComponent);